import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, ChevronRight, Loader2, Lock, ShoppingCart,
  User, Activity, Stethoscope, FileText, AlertTriangle, Heart, Mail
} from 'lucide-react';
import {
  calculateIMCSafe, calculateWaistHeightRatioSafe, evaluateAdiposityStatus,
  parseSafeNumber, calculateHeartRateZonesSafe as calculateHeartRateZones
} from '@/utils/calculations';
import { evaluateDecisionFE, getDecisionPresentation, intensityUnlock } from '@/utils/decisionState';
import { getScenarioLabel } from '@/utils/labels';
import { buildClinicalInfo } from '@/utils/clinicalInfo';
import { trackUsageEvent } from '@/lib/usageTracking';
import { trackInitiateCheckout } from '@/lib/metaPixel';
import { CURRENT_PRICE_ARS, PACK_PRICE_ARS, PACK_ENTREVISTAS, formatPriceARS } from '@/config/pricing';
// Componentes REALES del wizard — mismos que usa el alumno.
// Esto unifica: cualquier cambio en el wizard se refleja aquí sin sync manual.
import {
  Step1PersonalData,
  Step4Activity,
  Step6RiskFactors,
  Step3SafetyEvaluation,
} from '@/pages/NewEvaluationPage';

// Los 4 pasos coinciden con la entrevista real del alumno (sin limitaciones ni estudios).
const STEPS = [
  { label: 'Datos personales', icon: User },
  { label: 'Actividad física', icon: Activity },
  { label: 'Enfermedades y factores', icon: Heart },
  { label: 'Signos y síntomas', icon: Stethoscope },
];
const TOTAL_STEPS = 4;
// Steps virtuales por encima del wizard: 5 = email gate, 6 = resultado.
const EMAIL_GATE_STEP = 5;
const RESULT_STEP = 6;

const initialFormData = {
  student_name: '', age: '', sex: '', weight: '', height: '', waist_circumference: '',
  resting_heart_rate: '',
  symptom_chest_pain: null, symptom_dyspnea: null, symptom_dizziness: null,
  symptom_orthopnea: null, symptom_ankle_swelling: null, symptom_palpitations: null,
  symptom_intermittent_claudication: null, symptom_heart_murmur: null, symptom_unusual_fatigue: null,
  has_limitations: false, limitation_joints: [], pain_scale: 0, pain_type: '',
  limitation_observations: '',
  // Defaults for calculation compatibility
  activity_level: '', smoking: null, hypertension: null,
  diabetes: null, prediabetes: null, dyslipidemia: null, family_history: null,
};

function YesNoToggle({ value, onChange, id }) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          value === true ? 'bg-amber-100 text-amber-800 border-2 border-amber-400' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
        }`}
        data-testid={`${id}-yes`}
      >Sí</button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          value === false ? 'bg-green-100 text-green-800 border-2 border-green-400' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
        }`}
        data-testid={`${id}-no`}
      >No</button>
    </div>
  );
}

// Los steps 1-4 ahora usan los componentes reales exportados desde NewEvaluationPage.js
// (Step1PersonalData, Step4Activity, Step6RiskFactors, Step3SafetyEvaluation).
// Los 3 componentes locales duplicados anteriores (StepPersonalData/StepSymptoms/StepRiskFactors) fueron eliminados.

// Step 3 - Results: usa motor de 5 estados (paridad con Step7 wizard + EvaluationDetailPage)
function StepResults({ formData, onActivate, onPack, checkoutLoading }) {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  // Motor + presentación (mismos helpers que el informe final)
  const motorOutput = evaluateDecisionFE(formData);
  const presentation = getDecisionPresentation(motorOutput.estado);
  const motivos = motorOutput.motivos_decision;
  const unlock = intensityUnlock(motorOutput.estado);

  // Cálculos contextuales (intactos)
  const imc = calculateIMCSafe(formData.weight, formData.height);
  const whr = calculateWaistHeightRatioSafe(formData.waist_circumference, formData.height);
  const adiposity = evaluateAdiposityStatus(formData.waist_circumference, formData.height, formData.sex, formData.weight);
  const age = parseSafeNumber(formData.age) || 0;
  const restHR = parseSafeNumber(formData.resting_heart_rate) || 70;
  const hrZones = calculateHeartRateZones(age, restHR);

  // Información clínica relevante (mismo helper que EvaluationDetailPage)
  const clinicalInfo = buildClinicalInfo({
    ...formData,
    imc: parseSafeNumber(imc),
    waist_height_ratio: parseSafeNumber(whr),
    adiposity_status: adiposity ? (adiposity.status === 'normal' ? 'normal' : 'elevated_central') : null,
  });

  // Mapeo color del motor → clases tailwind para el banner del preview
  const banner = {
    rojo: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-900', accent: 'text-red-700' },
    naranja: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-900', accent: 'text-orange-700' },
    amarillo: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', accent: 'text-amber-700' },
    amarillo_claro: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', accent: 'text-yellow-700' },
    verde: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', accent: 'text-green-700' },
  }[presentation.bannerColor] || { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', accent: 'text-slate-500' };

  return (
    <div className="space-y-5" data-testid="step-results">
      {/* DEMO: Context label */}
      {!isLoggedIn && (
        <div className="text-center" data-testid="demo-mode-label">
          <span className="inline-block px-3 py-1 bg-slate-200 text-slate-600 text-xs font-medium rounded-full">
            Vista generada en modo demo
          </span>
        </div>
      )}

      {/* Banner driven by estado del motor */}
      <p
        className="text-xs sm:text-sm text-slate-600 text-center mb-2"
        data-testid="public-decision-preheader"
      >
        Según los datos recabados en la entrevista:
      </p>
      <div
        className={`p-5 rounded-xl text-center border-2 ${banner.bg} ${banner.border}`}
        data-testid="public-decision-banner"
      >
        <p className={`text-[10px] uppercase tracking-wider mb-1 ${banner.accent}`}>
          Recomendación operativa
        </p>
        <p
          className={`text-base sm:text-lg font-bold leading-tight ${banner.text}`}
          data-testid="public-decision-headline"
        >
          {presentation.headline}
        </p>
      </div>

      {/* Motivo de la recomendación */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="public-motivos-card">
        <p className="text-xs font-semibold text-slate-700 mb-2">Motivo de la recomendación</p>
        {motivos && motivos.length > 0 ? (
          <ul className="space-y-1.5 text-sm text-slate-700" data-testid="public-motivos-list">
            {motivos.map((m, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-slate-400" aria-hidden="true">•</span>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400 italic">Sin motivos disponibles.</p>
        )}
      </div>

      {/* Patient Info compact */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { l: 'Nombre', v: formData.student_name || '-' },
          { l: 'Edad', v: formData.age ? `${formData.age} años` : '-' },
          { l: 'IMC', v: imc ? `${imc} kg/m²` : '-' },
          { l: 'Cintura', v: formData.waist_circumference ? `${formData.waist_circumference} cm` : '-' },
        ].map(({ l, v }) => (
          <div key={l} className="p-3 bg-slate-50 rounded-lg text-center">
            <p className="text-[10px] text-slate-400 uppercase">{l}</p>
            <p className="text-sm font-semibold text-slate-800 truncate">{v}</p>
          </div>
        ))}
      </div>

      {/* Información clínica relevante (reemplaza Factores Identificados) */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="public-clinical-info">
        <p className="text-xs font-semibold text-slate-700">Información clínica relevante</p>
        <p className="text-[11px] text-slate-400 italic mt-0.5 mb-2">
          Datos no decisorios — para consideración profesional
        </p>
        {clinicalInfo.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Sin observaciones contextuales.</p>
        ) : (
          <ul className="space-y-1.5">
            {clinicalInfo.map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-slate-400 mt-0.5" aria-hidden="true">•</span>
                <span className="flex-1 break-words">
                  {it.label}
                  {it.value && <span className="font-semibold ml-1">{it.value}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Adiposity contextual note (mantenida) */}
      {adiposity && adiposity.status !== 'normal' && (
        <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-800 italic">
          {adiposity.description}
        </div>
      )}

      {/* Recomendaciones moderado / vigoroso por estado */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className={`p-3 rounded-lg border ${banner.bg} ${banner.border}`}>
          <p className="text-[10px] uppercase text-gray-500">Ejercicio moderado</p>
          <p className={`text-xs font-bold mt-1 ${banner.text}`} data-testid="public-rec-moderate">
            {presentation.moderate}
          </p>
        </div>
        <div className={`p-3 rounded-lg border ${banner.bg} ${banner.border}`}>
          <p className="text-[10px] uppercase text-gray-500">Ejercicio vigoroso</p>
          <p className={`text-xs font-bold mt-1 ${banner.text}`} data-testid="public-rec-vigorous">
            {presentation.vigorous}
          </p>
        </div>
      </div>

      {/* Zonas: solo las que habilita el escenario (mismo criterio que la app) */}
      {hrZones.moderateLow && (unlock.moderate.enabled || unlock.vigorous.enabled) && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Zonas de entrenamiento</p>
          <div className="grid grid-cols-2 gap-3">
            {unlock.moderate.enabled && (
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-[10px] text-green-600 uppercase">Moderada (50-70% FCR)</p>
                <p className="text-base font-bold text-green-800 mt-1">{hrZones.moderateLow} - {hrZones.moderateHigh} lpm</p>
              </div>
            )}
            {unlock.vigorous.enabled ? (
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-[10px] text-amber-600 uppercase">Vigorosa (70-85% FCR)</p>
                <p className="text-base font-bold text-amber-800 mt-1">{hrZones.vigorousLow} - {hrZones.vigorousHigh} lpm</p>
              </div>
            ) : (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-center opacity-70">
                <p className="text-[10px] text-gray-500 uppercase">Vigorosa</p>
                <p className="text-[11px] italic text-gray-500 mt-1">{unlock.vigorous.reason}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conducta operativa */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Conducta operativa</p>
        <div className={`p-3 rounded-lg border ${banner.bg} ${banner.border}`}>
          <p className={`text-sm font-medium ${banner.text}`}>{presentation.conducta}</p>
        </div>
      </div>

      {/* DEMO: Final CTA block (intacto) */}
      {!isLoggedIn && <DemoResultCTA onActivate={onActivate} onPack={onPack} loading={checkoutLoading} />}

      {/* Disclaimer */}
      <p className="text-[11px] text-slate-400 italic text-center">
        Este informe es orientativo y no reemplaza la evaluación médica profesional.
      </p>
    </div>
  );
}

// === DEMO CONVERSION COMPONENTS (ONLY for PublicEvaluationPage) ===

// 31/10/2026: todas las ofertas de la prueba van primero al pack de $9.900 (el
// escalón de entrada). El ilimitado queda como segunda opción en el resultado.
function DemoBanner({ onActivate, loading }) {
  return (
    <div className="bg-[#1F3A5F] text-white" data-testid="demo-top-banner">
      <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Versión de prueba de EVALPRO</p>
          <p className="text-white/70 text-xs mt-0.5">Probalo con {PACK_ENTREVISTAS} alumnos tuyos por {formatPriceARS(PACK_PRICE_ARS)}</p>
        </div>
        <button
          type="button"
          onClick={onActivate}
          disabled={loading}
          data-testid="demo-banner-cta"
          className="flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-[#1F3A5F] text-xs font-bold px-4 py-2 rounded-lg whitespace-nowrap hover:bg-slate-100 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Redirigiendo…</>
          ) : (
            <>Comprar el pack <ChevronRight className="h-3.5 w-3.5" /></>
          )}
        </button>
      </div>
    </div>
  );
}

function DemoResultCTA({ onActivate, onPack, loading }) {
  const cuota = formatPriceARS(Math.round(CURRENT_PRICE_ARS / 3));
  return (
    <div className="mt-6 p-5 bg-white border-2 border-[#1F3A5F]/15 rounded-xl" data-testid="demo-result-cta">
      <h3 className="text-base font-bold text-[#1F3A5F] text-center">Este es el informe que podés generar con cada alumno</h3>
      <p className="text-sm text-slate-600 leading-relaxed text-center mt-2">
        Estás viendo una entrevista de muestra. Con tu cuenta cargás a tus alumnos, guardás su ficha y les mandás el informe.
      </p>
      <div className="mt-4 text-center">
        <p className="text-sm font-semibold text-[#1F3A5F]">Probalo con {PACK_ENTREVISTAS} alumnos tuyos</p>
        <p className="text-3xl font-bold text-[#0A1845] mt-0.5">{formatPriceARS(PACK_PRICE_ARS)}</p>
        <p className="text-xs text-slate-500 mt-1.5">Pago único · Los retests de esas personas no gastan</p>
      </div>
      <Button
        onClick={onPack}
        disabled={loading}
        className="w-full mt-4 bg-[#5B9B2F] hover:bg-[#4a8126] text-white h-12 font-bold text-base disabled:opacity-70 disabled:cursor-not-allowed"
        data-testid="btn-results-activate"
      >
        {loading ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Redirigiendo…</>) : (<><ShoppingCart className="h-5 w-5 mr-2" /> Comprar el pack</>)}
      </Button>
      <p className="text-xs text-slate-500 text-center mt-2">Si después pasás al ilimitado, te descontamos lo que pagaste.</p>
      <div className="mt-4 pt-4 border-t border-slate-200 text-center" data-testid="demo-result-cta-helper">
        <p className="text-sm text-slate-600">
          ¿Tenés muchos alumnos? EVALPRO ilimitado: <span className="font-semibold">{formatPriceARS(CURRENT_PRICE_ARS)}</span> o 3 cuotas de {cuota} · Garantía 10 días
        </p>
        <button
          type="button"
          onClick={onActivate}
          disabled={loading}
          data-testid="demo-result-cta-btn"
          className="mt-1.5 inline-flex items-center gap-1 text-sm font-bold text-[#1F3A5F] hover:underline disabled:opacity-70"
        >
          Ver el ilimitado <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function DemoMidCTA({ onActivate, loading }) {
  return (
    <div className="mt-4 p-3 bg-[#1F3A5F]/5 border border-[#1F3A5F]/10 rounded-lg text-center" data-testid="demo-mid-cta">
      <p className="text-sm text-[#1F3A5F] font-medium">Esto es una entrevista de muestra. Probalo con {PACK_ENTREVISTAS} alumnos tuyos por {formatPriceARS(PACK_PRICE_ARS)}</p>
      <button
        type="button"
        onClick={onActivate}
        disabled={loading}
        data-testid="demo-mid-cta-link"
        className="inline-flex items-center gap-1 text-sm font-bold text-[#1F3A5F] mt-1 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {loading ? 'Redirigiendo…' : 'Comprar el pack'} <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// === MAIN COMPONENT ===
export default function PublicEvaluationPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [consent, setConsent] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  // Email gate state (paso 5, entre wizard y resultado)
  const [demoEmail, setDemoEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFullUser = user?.account_type === 'full';

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  // El wizard real (Step1PersonalData etc.) usa la prop `updateField`, no `update`.
  const updateField = update;

  const handleActivate = () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try { trackInitiateCheckout({ content_name: 'EVALPRO ilimitado', content_type: 'product', content_ids: ['ilimitado'] }); } catch (_) { /* noop */ }
    navigate('/checkout');
  };

  const handlePack = () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try { trackInitiateCheckout({ content_name: `Pack de ${PACK_ENTREVISTAS} entrevistas`, value: PACK_PRICE_ARS, content_type: 'product', content_ids: ['pack'] }); } catch (_) { /* noop */ }
    navigate('/checkout?producto=pack');
  };

  useEffect(() => {
    if (!isFullUser) trackUsageEvent('start_evaluation');
  }, [isFullUser]);

  useEffect(() => {
    if (!isFullUser && step === RESULT_STEP) {
      trackUsageEvent('complete_evaluation');
      trackUsageEvent('view_report');
    }
  }, [isFullUser, step]);

  // Submit del email gate. Guarda el lead (source=demo), dispara mail Resend
  // (fire-and-forget en backend) y revela el resultado sin fricción.
  const handleEmailSubmit = async (e) => {
    e?.preventDefault?.();
    const em = (demoEmail || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
      toast.error('Ingresá un email válido');
      return;
    }
    setEmailSubmitting(true);
    try {
      // Snapshot resumido para armar el mail — NO se persiste la entrevista.
      const motorOutput = evaluateDecisionFE(formData);
      const presentation = getDecisionPresentation(motorOutput.estado);
      await api.request('/lead/demo', {
        method: 'POST',
        body: JSON.stringify({
          email: em,
          scenario_label: getScenarioLabel(motorOutput.estado),
          scenario_desc: presentation.conducta || '',
          motivos: (motorOutput.motivos_decision || []).slice(0, 6),
        }),
      });
    } catch (err) {
      // Resend/mail failure NO bloquea. El lead ya quedó en DB; user ve el informe.
      console.error('[Demo lead]', err);
    } finally {
      setEmailSubmitting(false);
      setStep(RESULT_STEP);
    }
  };
  // Nota: handleDownloadPdf eliminado — la demo no descarga PDFs.

  const canAdvance = useMemo(() => {
    // Paso 1 · Datos personales
    if (step === 1) return consent && formData.student_name && formData.age && formData.sex && formData.weight && formData.height;
    // Paso 2 · Actividad física
    if (step === 2) return !!formData.activity_level;
    // Paso 3 · Factores de riesgo (5 respuestas obligatorias, no-null)
    if (step === 3) {
      return formData.smoking !== null && formData.hypertension !== null &&
             formData.diabetes !== null && formData.dyslipidemia !== null &&
             (formData.diabetes === true || formData.prediabetes !== null) &&
             formData.family_history !== null;
    }
    // Paso 4 · Signos y síntomas (9 respuestas no-null)
    if (step === 4) {
      const s = ['symptom_chest_pain','symptom_dyspnea','symptom_dizziness','symptom_orthopnea','symptom_ankle_swelling','symptom_palpitations','symptom_intermittent_claudication','symptom_heart_murmur','symptom_unusual_fatigue'];
      return s.every(k => formData[k] !== null);
    }
    return true;
  }, [step, formData, consent]);

  const totalSteps = TOTAL_STEPS;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* DEMO: Top banner - always visible */}
      {!isFullUser && <DemoBanner onActivate={handlePack} loading={checkoutLoading} />}

      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="bg-white rounded-[10px] p-1.5 shadow-sm border border-slate-200">
              <img src="/logo-evalpro.png" alt="EVALPRO" className="h-14 w-auto" />
            </div>
            <span className="font-bold text-[#04427d] text-base tracking-tight">EVALPRO</span>
          </div>
          {step <= TOTAL_STEPS && (
            <span className="text-xs text-slate-400" data-testid="step-counter">Paso {step} de {TOTAL_STEPS}</span>
          )}
        </div>
      </div>

      {/* Progress */}
      {step <= TOTAL_STEPS && (
        <div className="bg-white border-b border-slate-100 px-4 py-2">
          <div className="max-w-2xl mx-auto">
            <div className="flex gap-1">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < step ? 'bg-[#1F3A5F]' : 'bg-slate-200'}`} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`max-w-2xl mx-auto px-4 py-6 ${!isFullUser ? 'pb-28' : ''}`}>
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-slate-800 font-['Inter'] flex items-center gap-2">
              {step <= TOTAL_STEPS && React.createElement(STEPS[step - 1].icon, { className: 'h-5 w-5 text-[#1F3A5F]' })}
              {step === 1 && 'Datos personales'}
              {step === 2 && 'Actividad física habitual'}
              {step === 3 && 'Enfermedades y factores de riesgo cardiovascular'}
              {step === 4 && 'Signos y síntomas de alarma'}
              {step === EMAIL_GATE_STEP && (<><Mail className="h-5 w-5 text-[#1F3A5F]" />Tu informe está listo</>)}
              {step === RESULT_STEP && 'Resultado de la entrevista'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <>
                <Step1PersonalData formData={formData} updateField={updateField} hideGroups={true} hideResults={true} />
                <label className="flex items-start gap-2.5 mt-5 pt-4 border-t border-slate-200 cursor-pointer" data-testid="consent-checkbox-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#1F3A5F]"
                    data-testid="consent-checkbox"
                  />
                  <span className="text-xs text-slate-500 leading-relaxed">
                    Autorizo el uso de mis datos de salud para generar este informe (Ley 25.326)
                  </span>
                </label>
              </>
            )}
            {step === 2 && <Step4Activity formData={formData} updateField={updateField} />}
            {step === 3 && <Step6RiskFactors formData={formData} updateField={updateField} />}
            {step === 4 && <Step3SafetyEvaluation formData={formData} updateField={updateField} hideMedicalAlert={true} />}
            {step === EMAIL_GATE_STEP && (
              <form onSubmit={handleEmailSubmit} className="space-y-4 py-2" data-testid="demo-email-gate">
                <p className="text-sm text-slate-600 leading-relaxed">
                  Dejanos tu mail y te mostramos el resultado de la entrevista.
                </p>
                <div>
                  <Label htmlFor="demo-email" className="text-sm font-medium text-slate-700">Tu email</Label>
                  <Input
                    id="demo-email"
                    type="email"
                    value={demoEmail}
                    onChange={(e) => setDemoEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="mt-1.5 h-11"
                    required
                    autoFocus
                    data-testid="demo-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={emailSubmitting || !demoEmail}
                  className="w-full h-12 bg-[#5B9B2F] hover:bg-[#4a8126] text-white font-semibold disabled:opacity-70"
                  data-testid="demo-email-submit"
                >
                  {emailSubmitting ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Enviando…</>) : (<>Ver el informe <ArrowRight className="h-4 w-4 ml-1" /></>)}
                </Button>
                <p className="text-[11px] text-slate-500 text-center">Te lo enviamos también por mail. Sin spam.</p>
              </form>
            )}
            {step === RESULT_STEP && <StepResults formData={formData} onActivate={handleActivate} onPack={handlePack} checkoutLoading={checkoutLoading} />}
          </CardContent>
        </Card>

        {/* Conversion banner - shown right before email gate for non-full users */}
        {!isFullUser && step === 4 && <DemoMidCTA onActivate={handlePack} loading={checkoutLoading} />}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          {step > 1 && step !== RESULT_STEP ? (
            <Button variant="ghost" onClick={() => setStep(s => s - 1)} className="text-slate-600" data-testid="btn-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Atrás
            </Button>
          ) : <div />}

          {step < TOTAL_STEPS ? (
            <Button onClick={() => setStep(s => s + 1)} disabled={!canAdvance} className="bg-[#1F3A5F] hover:bg-[#2a4a73] text-white" data-testid="btn-next">
              Siguiente <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : step === TOTAL_STEPS ? (
            <Button onClick={() => setStep(EMAIL_GATE_STEP)} disabled={!canAdvance} className="bg-[#5B9B2F] hover:bg-[#4a8126] text-white" data-testid="btn-see-report">
              Ver el informe <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : step === RESULT_STEP ? (
            <div className="flex gap-3">
              <Button onClick={() => { setStep(1); setFormData(initialFormData); setConsent(false); setDemoEmail(''); }} variant="outline" className="text-slate-600" data-testid="btn-new-evaluation">
                Nueva entrevista
              </Button>
              <Button 
                onClick={handlePack}
                disabled={checkoutLoading}
                className="bg-[#5B9B2F] hover:bg-[#4a8126] text-white"
                data-testid="btn-activate-full"
              >
                {checkoutLoading ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Redirigiendo…</>) : (<><ShoppingCart className="h-4 w-4 mr-1" /> Comprar el pack</>)}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Sticky conversion banner - all steps for non-full users */}
      {!isFullUser && (
        <div className="fixed bottom-0 left-0 right-0 z-50" data-testid="conversion-banner">
          <div className="bg-gradient-to-r from-[#1a7a2e] to-[#22912e] shadow-[0_-4px_20px_rgba(0,0,0,0.15)]">
            <div className="max-w-2xl mx-auto px-4 pt-3 pb-1.5 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold leading-tight">Probalo con {PACK_ENTREVISTAS} alumnos tuyos</p>
                <p className="text-white/90 text-xs mt-1">
                  <span className="font-bold">{formatPriceARS(PACK_PRICE_ARS)}</span> pago único · acceso inmediato
                </p>
              </div>
              <button
                type="button"
                onClick={handlePack}
                disabled={checkoutLoading}
                data-testid="conversion-banner-cta"
                className="flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-[#1a7a2e] text-xs font-bold px-5 py-2.5 rounded-lg whitespace-nowrap transition-transform hover:scale-105 active:scale-95 shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {checkoutLoading ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Redirigiendo…</>
                ) : (
                  'Comprar el pack'
                )}
              </button>
            </div>
            <p className="text-center text-white/50 text-[10px] pb-2">Si después pasás al ilimitado, te descontamos lo que pagaste</p>
          </div>
        </div>
      )}
    </div>
  );
}
